import { useState } from "react";
import { CalendarX, ArrowsClockwise } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import Modal, { GhostBtn, PrimaryBtn } from "@/components/Modal";
import { useI18n, fmtDate } from "@/i18n/I18nContext";
import { toast } from "sonner";


export function SubscriptionNotice({ info, onCancel, onResumed }) {
  const { t, lang } = useI18n();
  const [busy, setBusy] = useState(false);
  if (info.payment_provider !== "onvo" || info.billing_status !== "active") return null;
  const end = fmtDate(info.current_period_end, lang);

  const resume = async () => {
    setBusy(true);
    try { await api.post("/onvo/resume"); toast.success(t.billing.resumed); await onResumed(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 mt-5 pt-5 border-t border-[#E5E2DC]" data-testid="subscription-notice">
      <p className="text-sm text-[#2C3E35]" data-testid="subscription-notice-text">
        {info.cancel_at_period_end ? t.billing.cancelScheduled.replace("{date}", end)
          : info.pending_plan ? t.billing.switchScheduled.replace("{plan}", info.pending_plan).replace("{date}", end)
          : t.billing.autoRenew.replace("{date}", end)}
      </p>
      {info.cancel_at_period_end ? (
        <button data-testid="resume-subscription" disabled={busy} onClick={resume} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#2C3E35] hover:text-[#B86B5A] transition-colors disabled:opacity-50">
          <ArrowsClockwise size={15} /> {t.billing.resume}
        </button>
      ) : (
        <button data-testid="cancel-subscription" onClick={onCancel} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#B86B5A] hover:text-[#8f4f41] transition-colors">
          <CalendarX size={15} /> {t.billing.cancel}
        </button>
      )}
    </div>
  );
}

export function ConfirmPlanModal({ action, info, onClose, onDone }) {
  const { t, lang } = useI18n();
  const [busy, setBusy] = useState(false);
  if (!action) return null;
  const end = fmtDate(info.current_period_end, lang);
  const isCancel = action.type === "cancel";
  const upgrade = action.plan === "elite";
  const body = isCancel ? t.billing.cancelBody.replace("{date}", end)
    : upgrade ? t.billing.upgradeBody.replace("{date}", end) : t.billing.downgradeBody.replace("{date}", end);

  const confirm = async () => {
    setBusy(true);
    try {
      if (isCancel) await api.post("/onvo/cancel");
      else await api.post("/onvo/change-plan", { plan: action.plan });
      toast.success(isCancel ? t.billing.cancelDone : t.billing.changeDone);
      onClose();
      await onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <Modal open onClose={onClose} size="sm" testid="confirm-plan-modal"
      title={isCancel ? t.billing.cancelTitle : t.billing.changeTitle.replace("{plan}", action.plan === "elite" ? "Elite" : "Basic")}
      footer={<>
        <GhostBtn data-testid="confirm-plan-back" onClick={onClose}>{t.billing.back}</GhostBtn>
        <PrimaryBtn data-testid="confirm-plan-submit" disabled={busy} onClick={confirm}>{busy ? "…" : isCancel ? t.billing.cancelConfirm : t.billing.changeConfirm}</PrimaryBtn>
      </>}>
      <p className="text-sm text-[#2C3E35] leading-relaxed" data-testid="confirm-plan-body">{body}</p>
    </Modal>
  );
}
